import React from 'react';
import { Loader, Message } from '@getro/rombo';
import PropTypes from 'prop-types';
import { Route, Switch, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useContactsExtended } from 'pages/contactsExtended/hooks/useContactsExtended';
import { ExtendedContact } from 'pages/contactsExtended/extendedContact';
import { ListContactsEmptyState } from './listContactsEmptyState';
import usePageTitle from '../../hooks/usePageTitle';
import { CreateList } from '../list/createList';

const DuplicatingListMessageTitle = styled.h5`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.dark};
`;

export const ContactList = ({ isJobList, sharedList }) => {
  const {
    listId,
    queryParams,
    selectedRows,
    setSelectedRows,
    data,
    customFields,
    isLoading,
    isFetching,
    filtersCount,
    meta,
    showAllContactsEmptyState,
    onLoadMore,
    onChangeSelectedContact,
    onSetFilters,
    activeNetwork,
  } = useContactsExtended({ allList: false, page: 'list' });
  const { name, smartOptions, refreshInProgress, operationInProgress } = sharedList;
  const isDuplicatingList =
    operationInProgress?.operationType === 'duplicate_shared_list' && operationInProgress?.status === 'pending';
  const autoUpdate = smartOptions?.includes('auto_update');
  const history = useHistory();
  usePageTitle(name);

  return (
    <>
      {(isDuplicatingList || (refreshInProgress && data?.ids?.length > 0)) && (
        <div
          style={{
            marginBottom: '32px',
            padding: '0 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {isDuplicatingList && (
            <Message>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <DuplicatingListMessageTitle>Duplicating list...</DuplicatingListMessageTitle>
                <p style={{ margin: 0 }}>This can take a few minutes, come back soon to see updates.</p>
              </div>
            </Message>
          )}
          {refreshInProgress && data?.ids?.length > 0 && (
            <Message>Auto-update in progress, come back soon to see updates (it may take a few minutes).</Message>
          )}
        </div>
      )}
      <Switch>
        <Route exact path={`/networks/${activeNetwork.slug}/list/:listId/contacts/lists/settings`}>
          <CreateList
            goBack={() => {
              history.push(`/networks/${activeNetwork.slug}/list/${listId}/contacts${history.location.search}`);
            }}
          />
        </Route>
        <Route>
          {isLoading ? (
            <Loader />
          ) : (
            <ExtendedContact
              onSetFilters={onSetFilters}
              queryParams={queryParams}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              onChangeSelectedContact={onChangeSelectedContact}
              onLoadMore={onLoadMore}
              data={data?.ids?.map((id) => data?.entities[id]) ?? []}
              customFields={customFields}
              isLoading={isLoading}
              isFetching={isFetching}
              meta={meta}
              filtersCount={filtersCount}
              activeNetwork={activeNetwork}
              showAllContactsEmptyState={showAllContactsEmptyState}
              listId={listId}
              emptyStateUrl={`/networks/${activeNetwork.slug}/list/${listId}/add-contacts`}
              emptyState={
                <ListContactsEmptyState
                  isJobList={isJobList}
                  listId={listId}
                  autoUpdate={autoUpdate}
                  refreshInProgress={refreshInProgress}
                />
              }
              showListFilters={false}
              deleteContactFromList
              list={sharedList}
              filtersTitle="Filter list"
              bulkActions={['add-to-list', 'add-tag', 'export', 'delete', 'clear-selection']}
              page="list"
              tableSx={{
                height: 'calc(100vh - 133px)',
              }}
              cardSx={{ height: 'calc(100dvh - 133px)' }}
            />
          )}
        </Route>
      </Switch>
    </>
  );
};

ContactList.propTypes = {
  isJobList: PropTypes.bool.isRequired,
  sharedList: PropTypes.object,
};

ContactList.defaultProps = {
  sharedList: {},
};
